import React, { useState } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { qaAttribute } from '@gumtree/ui-library/src/utils/qa-service';
import {
    trackGA4AndNavigate,
    trackGA4Event,
    trackGA4UserLoginSuccess,
    trackGA4UserRegistrationSuccess,
} from '@gumtree/shared/src/util/ga4-shared';
import { getLinkHostname } from '@gumtree/shared/src/util/ga4-bff';
import { getDomainFromUrl } from '@gumtree/shared/src/util/url-service';
import { trackV2 } from '@gumtree/shared/src/util/track-ga-event';
import { notifyHomePostBannerHeaderClick } from '@gumtree/shared/src/util/home-post-banner-events';
import SignupIcon from '@gumtree/ui-library/src/icon/svg/homepage/signup.svg';
import LoginIcon from '@gumtree/ui-library/src/icon/svg/homepage/login.svg';
import DiscountTips from '../discount-tips';

import type { ShellState, ShellUseDispatch } from '../../../reducers/common';

import SearchBar from '../../search-bar/search-bar-container';
import NavBarMessagesButton from '../nav-bar-messages-button';
import UserMenu from '../../user-menu/user-menu';
import LoginModal from '../../../login-modal/app';
import { StyledDialog } from '../../../login-modal/app.style';
import { useModal } from '../../../use-modal';
import {
    LoginModalType,
    setIsOpen,
    showForgotPasswordModal,
} from '../../../reducers/common/login-modal';

import {
    NavContainer,
    NavLogo,
    SearchBarContainer,
    RightActions,
    ActionButton,
    ActionLabel,
    ActionIconWrapper,
    PostAdButton,
    PostAdLink,
} from './mhc-desktop-nav-bar.style';

interface Props {
    onMenuOpenChange?: (isOpen: boolean) => void;
}

export default function MhcDesktopNavBar({ onMenuOpenChange }: Props) {
    const [focusSearchBar, setFocusSearchBar] = useState(false);
    const { openDialog, closeDialog } = useModal();
    const { baseConfig, userData, isOpen } = useSelector(
        ({ baseConfig, userData, loginModal: { isOpen } }: ShellState) => ({
            baseConfig,
            userData,
            isOpen,
        }),
        shallowEqual
    );

    const dispatch = useDispatch() as ShellUseDispatch;
    const loggedIn = userData && userData.userLoggedIn;
    const pageType = baseConfig?.pageType;

    React.useEffect(() => {
        trackGA4UserLoginSuccess();
        trackGA4UserRegistrationSuccess();

        const params = new URLSearchParams(window.location.search);
        const resetPassword = params.get('reset-password');
        const isRegistrationSuccessful = params.get('new-registration');

        if (isRegistrationSuccessful) {
            dispatch(setIsOpen(true));
            params.delete('new-registration');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        } else if (resetPassword) {
            dispatch(setIsOpen(true));
            dispatch(showForgotPasswordModal({ value: true, isReset: true }));
            params.delete('reset-password');
            const newUrl = `${window.location.pathname}?${params.toString()}`;
            window.history.replaceState({}, '', newUrl);
        }
    }, []);

    const openSearchBar = () => {
        closeDialog();
        setFocusSearchBar(true);
    };

    const handlePostAdClick = (e: React.MouseEvent) => {
        notifyHomePostBannerHeaderClick();
        if (loggedIn) {
            const url = '/postad/category';
            trackGA4AndNavigate<GA4.ClickPrimaryNavigation>({
                href: url,
                mouseEvent: e,
                payload: {
                    event: 'click_primary_navigation',
                    linkDomain: getDomainFromUrl(url),
                    linkText: 'Post Ad',
                    linkURL: url,
                    navigationCategory: 'menu',
                    navigationHeader: undefined,
                    navigationHierarchy: 'header',
                    headerCtaClicked: 'new listing',
                    pageClickedFrom: pageType,
                },
            });
        } else {
            openDialog(e, LoginModalType.SELL, true);
            trackV2('PostAdBegin', 'PostAdHeader');
            trackGA4Event<GA4.ClickPrimaryNavigation>({
                event: 'click_primary_navigation',
                linkText: 'Post Ad',
                navigationCategory: 'menu',
                navigationHeader: undefined,
                navigationHierarchy: 'header',
                headerCtaClicked: 'new listing',
                pageClickedFrom: pageType,
            });
        }
    };

    return (
        <NavContainer data-q="nav-bar" data-testid="mhc-desktop-nav-bar">
            <NavLogo
                linkTo="/"
                {...qaAttribute('gumtree-logo')}
                data-testid="gumtree-logo"
                onClick={(e) =>
                    trackGA4AndNavigate<GA4.ClickPrimaryLogo>({
                        href: '/',
                        mouseEvent: e,
                        payload: {
                            event: 'click_primary_logo',
                            linkDomain: getLinkHostname('/'),
                            linkText: 'Gumtree',
                            linkURL: '/',
                        },
                    })
                }
            />

            <SearchBarContainer>
                <SearchBar outsideFocus={focusSearchBar} />
            </SearchBarContainer>

            <RightActions>
                {loggedIn ? (
                    <>
                        <NavBarMessagesButton isMhcDesktopExperiment />
                        <UserMenu
                            isLoggedIn
                            isMhcDesktopExperiment
                            onOpenChange={onMenuOpenChange}
                            pageType={pageType}
                        />
                    </>
                ) : (
                    <>
                        <ActionButton
                            {...qaAttribute('hm-registration')}
                            onClick={(e) => {
                                openDialog(e, LoginModalType.REGISTER, false);
                                trackGA4Event<GA4.ClickPrimaryNavigation>({
                                    event: 'click_primary_navigation',
                                    linkText: 'sign_up',
                                    navigationCategory: 'menu',
                                    navigationHeader: undefined,
                                    navigationHierarchy: 'header',
                                    pageClickedFrom: pageType,
                                });
                            }}
                            aria-label="Sign up"
                        >
                            <ActionIconWrapper>
                                <SignupIcon />
                            </ActionIconWrapper>
                            <ActionLabel>Sign up</ActionLabel>
                        </ActionButton>
                        <ActionButton
                            {...qaAttribute('hm-login')}
                            onClick={(e) => {
                                openDialog(e, LoginModalType.LOGIN, true);
                                trackGA4Event<GA4.ClickPrimaryNavigation>({
                                    event: 'click_primary_navigation',
                                    linkText: 'log_in',
                                    navigationCategory: 'menu',
                                    navigationHeader: undefined,
                                    navigationHierarchy: 'header',
                                    pageClickedFrom: pageType,
                                });
                            }}
                            aria-label="Login"
                        >
                            <ActionIconWrapper>
                                <LoginIcon />
                            </ActionIconWrapper>
                            <ActionLabel>Login</ActionLabel>
                        </ActionButton>
                    </>
                )}
                {loggedIn ? (
                    <DiscountTips enabled={pageType === 'Homepage'}>
                        <PostAdLink
                            href="/postad/category"
                            {...qaAttribute('post-ad-button')}
                            data-testid="post-ad-button"
                            onClick={handlePostAdClick}
                        >
                            Post Ad
                        </PostAdLink>
                    </DiscountTips>
                ) : (
                    <PostAdButton
                        type="button"
                        {...qaAttribute('post-ad-button')}
                        data-testid="post-ad-button"
                        onClick={handlePostAdClick}
                    >
                        Post Ad
                    </PostAdButton>
                )}
            </RightActions>

            <StyledDialog isOpen={isOpen} onClose={closeDialog}>
                <LoginModal openSearchBar={openSearchBar} />
            </StyledDialog>
        </NavContainer>
    );
}
